import { useEffect, useState } from 'react';
import { api } from '@/api/growth/dashboard/api';
import {
    GrowthDashboardReq,
    SoilTestListItem,
} from '@/api/growth/dashboard/type';
import GrowthDashboardSoilTextureTriangleCard from './GrowthDashboardSoilTextureTriangleCard';
import GrowthDashboardPawcCard from './GrowthDashboardPawcCard';

interface GrowthDashboardSoilInfoCardProps {
    gfIdx: number;
}

const GrowthDashboardSoilInfoCard = ({
    gfIdx,
}: GrowthDashboardSoilInfoCardProps) => {
    const [loading, setLoading] = useState(true);
    const [soilTestList, setSoilTestList] = useState<SoilTestListItem[]>([]);
    const [selectedLayer, setSelectedLayer] = useState<number>(0);
    const [optimalOne, setOptimalOne] = useState<string>('');
    const [optimalTwo, setOptimalTwo] = useState<string | null>(null);
    const [waterState, setWaterState] = useState<string | undefined>(undefined);

    useEffect(() => {
        fetchSoilInfo();
    }, [gfIdx]);

    const fetchSoilInfo = async () => {
        setLoading(true);
        try {
            const req: GrowthDashboardReq = { gfIdx };
            const response = await api.getSoilInfo(req);

            if (
                response.data &&
                response.data.soilTestList &&
                response.data.soilTestList.length > 0
            ) {
                // depth 기준으로 정렬
                const sortedList = [...response.data.soilTestList].sort(
                    (a, b) => a.depth - b.depth,
                );
                setSoilTestList(sortedList);
                setSelectedLayer(0);

                setOptimalOne(response.data.optimalOne || '');
                setOptimalTwo(response.data.optimalTwo || null);
                setWaterState(response.data.waterState ?? undefined);
            }
        } catch (error) {
            console.error('토양 정보 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleLayerChange = (index: number) => {
        setSelectedLayer(index);
    };

    // 토성 한글 변환
    const getSoilTextureKorean = (texture: string) => {
        const textureMap: { [key: string]: string } = {
            sand: '사토',
            'loamy sand': '사양토',
            'sandy loam': '사양토',
            loam: '양토',
            'silt loam': '미사질양토',
            silt: '미사토',
            'sandy clay loam': '식양토',
            'clay loam': '식양토',
            'silty clay loam': '미사질식양토',
            'sandy clay': '사질식토',
            'silty clay': '미사질식토',
            clay: '식토',
        };
        return textureMap[texture.toLowerCase()] || texture;
    };

    if (loading) {
        return (
            <div className="card custom-card p-2">
                <div className="card-body text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">로딩중...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!soilTestList || soilTestList.length === 0) {
        return (
            <div className="card custom-card p-2">
                <div className="card-body text-center p-5">
                    <div
                        className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light mb-3"
                        style={{ width: '80px', height: '80px' }}
                    >
                        <i
                            className="bi bi-clipboard-data text-muted"
                            style={{ fontSize: '2.5rem' }}
                        ></i>
                    </div>
                    <h6 className="text-muted mb-1">토양 정보 없음</h6>
                    <p className="text-muted small mb-0">
                        토양 분석 데이터가 없습니다
                    </p>
                </div>
            </div>
        );
    }

    const currentSoil = soilTestList[selectedLayer];

    return (
        <div className="row mt-2">
            <div className="col-12">
                <div className="card custom-card p-2 weather-card">
                    <div className="card-header">
                        <div className="w-100">
                            <h4 className="mb-1">토양샘플링결과</h4>
                            <div className="d-flex justify-content-between flex-wrap">
                                <div>
                                    <div className="lunar-date text-gray-7">
                                        시험일자: {currentSoil.testDt}
                                    </div>
                                    <div className="lunar-date text-gray-7">
                                        등록일자: {currentSoil.regDt}
                                    </div>
                                </div>
                                <div
                                    aria-label="Basic example"
                                    role="group"
                                    className="flex-wrap btn-group btn-group-sm mt-2 mt-md-0"
                                >
                                    {soilTestList.map((soil, index) => (
                                        <button
                                            key={soil.stIdx}
                                            type="button"
                                            className={`btn-wave btn ${
                                                selectedLayer === index
                                                    ? 'btn-success'
                                                    : 'btn-success-light'
                                            }`}
                                            onClick={() =>
                                                handleLayerChange(index)
                                            }
                                        >
                                            {index + 1}층({soil.depth}cm)
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card-body weather-info">
                        <h6>토양성분</h6>
                        <div className="row weather-grid">
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                pH
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.ph.toFixed(1)}
                            </div>
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                EC (전기전도도)
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.ec.toFixed(2)} dS/m
                            </div>
                        </div>

                        <div className="row weather-grid">
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                유기물함량
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.organicMatter.toFixed(2)} %
                            </div>
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                TOC (전유기탄소)
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.toc.toFixed(2)} %
                            </div>
                        </div>

                        <div className="row weather-grid">
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                T-N (전질소)
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.totalNitrogen.toFixed(3)} %
                            </div>
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                NH₄⁺ (암모니아)
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.ammonia.toFixed(2)} mg/kg
                            </div>
                        </div>

                        <div className="row weather-grid">
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                NO₃⁻ (질산)
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.nitricAcid.toFixed(2)} mg/kg
                            </div>
                            <div className="col-12 col-sm-3 weather-item weather-item-header"></div>
                            <div className="col-12 col-sm-3 weather-item"></div>
                        </div>

                        <h6 className="mt-2">염기포화도</h6>
                        <div className="row weather-grid">
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                CEC (양이온치환용량)
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.cec.toFixed(2)} cmol/kg
                            </div>
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                치환성 Ca
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.exchangeableCa.toFixed(2)} mg/kg
                            </div>
                        </div>

                        <div className="row weather-grid">
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                치환성 Mg
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.exchangeableMg.toFixed(2)} mg/kg
                            </div>
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                치환성 K
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.exchangeableK.toFixed(2)} mg/kg
                            </div>
                        </div>

                        <div className="row weather-grid">
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                P₂O₅ (유효인산)
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.availablePhosphorus.toFixed(2)}{' '}
                                mg/kg
                            </div>
                            <div className="col-12 col-sm-3 weather-item weather-item-header"></div>
                            <div className="col-12 col-sm-3 weather-item"></div>
                        </div>

                        <h6 className="mt-2">용적밀도</h6>
                        <div className="row weather-grid">
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                용적밀도
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.bulkDensity.toFixed(2)} g/cm³
                            </div>
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                고상
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.solid.toFixed(2)} %
                            </div>
                        </div>

                        <div className="row weather-grid">
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                액상
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.liquid.toFixed(2)} %
                            </div>
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                기상
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.air.toFixed(2)} %
                            </div>
                        </div>

                        <div className="row weather-grid">
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                공극률
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.porosity.toFixed(2)} %
                            </div>
                            <div className="col-12 col-sm-3 weather-item weather-item-header"></div>
                            <div className="col-12 col-sm-3 weather-item"></div>
                        </div>

                        <h6 className="mt-2">토양 비율</h6>
                        <div className="row weather-grid">
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                Soil texture(토성)
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {getSoilTextureKorean(currentSoil.soilTexture)}
                            </div>
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                Sand(사토)
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.sand.toFixed(2)} %
                            </div>
                        </div>

                        <div className="row weather-grid">
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                Silt(미사토)
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.silt.toFixed(2)} %
                            </div>
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                Clay(식토)
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.clay.toFixed(2)} %
                            </div>
                        </div>

                        <div className="row weather-grid">
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                수분함량
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.moistureMatter.toFixed(1)} %
                            </div>
                            <div className="col-12 col-sm-3 weather-item weather-item-header"></div>
                            <div className="col-12 col-sm-3 weather-item"></div>
                        </div>

                        <h6 className="mt-2">수분 한계점</h6>
                        <div className="row weather-grid">
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                가뭄점(영구위조점)
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.droughtPoint.toFixed(3)} m/m
                            </div>
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                생육저해점
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.drainLimitPoint.toFixed(3)} m/m
                            </div>
                        </div>

                        <div className="row weather-grid">
                            <div className="col-12 col-sm-3 weather-item weather-item-header">
                                침수점
                            </div>
                            <div className="col-12 col-sm-3 weather-item">
                                {currentSoil.floodPoint.toFixed(3)} m/m
                            </div>
                            <div className="col-12 col-sm-3 weather-item weather-item-header"></div>
                            <div className="col-12 col-sm-3 weather-item"></div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-md-6">
                <GrowthDashboardSoilTextureTriangleCard
                    currentSoil={currentSoil}
                    optimalOne={optimalOne}
                    optimalTwo={optimalTwo}
                />
            </div>
            <div className="col-12 col-md-6">
                <GrowthDashboardPawcCard soilTestList={soilTestList} waterState={waterState} />
            </div>
        </div>
    );
};

export default GrowthDashboardSoilInfoCard;
